import { CommonHelperFunctions, EDatasourceType } from "@kuika/kuika-cl-model";
import type { IAction, IDisplayItem, IEvent, IScreenDesign } from "@kuika/kuika-cl-model";
import type { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";

/** The parts of the designer context an action result can be produced in. */
export type ResultActionTagContext = Pick<
  IScreenDesignContextState,
  | "getScreenDesign"
  | "masterSd"
  | "isDocumentParameterAuthoringActive"
  | "documentParameterEvent"
  | "getSelectedApiMethod"
  | "getSelectedWfStep"
  | "activeEditingDatasource"
  | "getSelectedBluetoothMethod"
>;

type ActionSource = () => readonly IAction[] | undefined;

/**
 * The tag of the action a picked action result comes from, or undefined when it has none.
 *
 * The picker's dropdown shows the tag beside each result. That is the only way to tell two Get From
 * Bag results apart, because Get From Bag fills its tag in from the bag key. The picked symbol keeps
 * only `resultActionId` and the bare `[GetFromBag]` description, so anything that shows the value
 * afterwards has to read the tag back from the action (KOA-16209). The tag is not written into the
 * description: codegen string-matches it (`ClientCodeGenerationBase`), and a bag key changed later
 * would leave a copied tag stale.
 *
 * The search covers the places the picker offers results from (`DesignerSymbolPickerEngine.getAllActions`),
 * including actions nested in If / ForEach / Recurring / MQTT. Action ids are GUIDs, so looking in every
 * source cannot pick up another context's action. Scheduled jobs are left out because tags are not
 * offered there (KOA-14359).
 */
export function getResultActionTag(
  context: ResultActionTagContext,
  resultActionId: string | undefined
): string | undefined {
  if (!resultActionId) return undefined;
  const action: IAction | undefined = findResultAction(context, resultActionId);
  const tag: string | undefined = action?.tag?.trim();
  return tag ? tag : undefined;
}

function findResultAction(context: ResultActionTagContext, resultActionId: string): IAction | undefined {
  const screen: IScreenDesign | undefined = context.getScreenDesign();
  const fromScreen: IAction | undefined =
    findActionInDisplayItems(screen?.displayItems, resultActionId) ??
    findActionInDisplayItems(context.masterSd?.displayItems, resultActionId);
  if (fromScreen) return fromScreen;

  const sources: ActionSource[] = [
    (): readonly IAction[] | undefined =>
      context.isDocumentParameterAuthoringActive() ? context.documentParameterEvent?.actions : undefined,
    (): readonly IAction[] | undefined => context.getSelectedApiMethod()?.actions,
    (): readonly IAction[] | undefined => context.getSelectedWfStep()?.actions,
    // Reading the method clones and parses it, so it is only read while a BLE datasource is open,
    // the same gate the picker uses.
    (): readonly IAction[] | undefined =>
      context.activeEditingDatasource?.datasourceType === EDatasourceType.BleService
        ? context.getSelectedBluetoothMethod()?.bluetoothInfo?.actions
        : undefined
  ];
  for (const source of sources) {
    const hit: IAction | undefined = findActionInActions(source(), resultActionId);
    if (hit) return hit;
  }
  return undefined;
}

function findActionInDisplayItems(
  displayItems: readonly IDisplayItem[] | undefined,
  actionId: string
): IAction | undefined {
  if (!displayItems) return undefined;
  for (const di of displayItems) {
    const events: IEvent[] = di?.item?.events ?? [];
    for (const event of events) {
      const hit: IAction | undefined = findActionInActions(event?.actions, actionId);
      if (hit) return hit;
    }
    const childHit: IAction | undefined = findActionInDisplayItems(di?.children, actionId);
    if (childHit) return childHit;
  }
  return undefined;
}

function findActionInActions(actions: readonly IAction[] | undefined, actionId: string): IAction | undefined {
  if (!actions) return undefined;
  for (const action of actions) {
    if (!action) continue;
    const hit: IAction | undefined = CommonHelperFunctions.getChildActionFromAction(action).find(
      (candidate: IAction): boolean => candidate?.id === actionId
    );
    if (hit) return hit;
  }
  return undefined;
}
